import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Loader2, MapPin, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  createPlaceSessionToken,
  fetchPlacePredictions,
  resolvePlaceDetails,
  resolveEasyModePlaceDetails,
  type LocationSearchMode,
  type PlacePrediction,
} from "@/services/googlePlacesRestService";
import { toLocationDisplayLabel } from "@/utils/places/normalizeGooglePlace";
import type { DailyCommuteLocation } from "@/types/dailyCommuteLocation";
import type { EasyModePlaceSelection } from "@/types/easyModePlace";

const DEBOUNCE_MS = 300;
const MIN_INPUT_LENGTH = 2;
const MAX_SUGGESTIONS_DEFAULT = 5;
const MAX_SUGGESTIONS_EASY_MODE = 15;

export type { DailyCommuteLocation, SelectedPlace } from "@/types/dailyCommuteLocation";

type AutocompleteValue = DailyCommuteLocation | EasyModePlaceSelection | null;

interface LocationAutocompleteProps {
  id: string;
  label: string;
  placeholder: string;
  mode: LocationSearchMode;
  restrictToCountryCode?: string;
  value?: AutocompleteValue;
  onPlaceSelect?: (place: DailyCommuteLocation) => void;
  /** Find Ride / Easy Mode — city + area (sc / slc) extraction */
  onEasyModePlaceSelect?: (place: EasyModePlaceSelection) => void;
  onClear?: () => void;
  onInteraction?: () => void;
  clearable?: boolean;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  hideLabel?: boolean;
  pinClassName?: string;
  inputClassName?: string;
  fieldClassName?: string;
  endAdornment?: ReactNode;
}

function valueDisplayLabel(value: AutocompleteValue): string {
  if (!value) return "";
  if ("address" in value && value.address) return value.address;
  return toLocationDisplayLabel(value as DailyCommuteLocation);
}

export function LocationAutocomplete({
  id,
  label,
  placeholder,
  mode,
  restrictToCountryCode,
  value,
  onPlaceSelect,
  onEasyModePlaceSelect,
  onClear,
  onInteraction,
  clearable = true,
  className,
  disabled = false,
  isLoading = false,
  hideLabel = false,
  pinClassName,
  inputClassName,
  fieldClassName,
  endAdornment,
}: LocationAutocompleteProps) {
  const easyMode = Boolean(onEasyModePlaceSelect);
  const maxSuggestions = easyMode
    ? MAX_SUGGESTIONS_EASY_MODE
    : MAX_SUGGESTIONS_DEFAULT;
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const syncedPlaceIdRef = useRef<string | null>(null);
  const sessionTokenRef = useRef<string | null>(null);

  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");

  const clearDebounce = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const resetSuggestions = useCallback(() => {
    setPredictions([]);
    setIsOpen(false);
  }, []);

  useEffect(() => {
    return () => clearDebounce();
  }, []);

  useEffect(() => {
    const placeId = value?.placeId ?? null;
    if (placeId === syncedPlaceIdRef.current) return;

    syncedPlaceIdRef.current = placeId;
    if (value) {
      const nextLabel = valueDisplayLabel(value);
      setQuery(nextLabel);
      setSelectedLabel(nextLabel);
    } else {
      setQuery("");
      setSelectedLabel("");
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        resetSuggestions();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [resetSuggestions]);

  useEffect(() => {
    clearDebounce();
    requestIdRef.current += 1;

    const trimmed = query.trim();
    if (trimmed.length === 0) {
      sessionTokenRef.current = null;
    }

    if (trimmed.length < MIN_INPUT_LENGTH || trimmed === selectedLabel) {
      setIsSearching(false);
      resetSuggestions();
      return;
    }

    const requestId = requestIdRef.current;
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      setError(null);

      try {
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = createPlaceSessionToken();
        }
        const activeSessionToken = sessionTokenRef.current;

        const results = await fetchPlacePredictions(
          trimmed,
          mode,
          restrictToCountryCode,
          activeSessionToken
        );

        if (requestId !== requestIdRef.current) return;

        setPredictions(results.slice(0, maxSuggestions));
        setIsOpen(results.length > 0);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(
          err instanceof Error ? err.message : "Location search unavailable"
        );
        resetSuggestions();
      } finally {
        if (requestId === requestIdRef.current) {
          setIsSearching(false);
        }
      }
    }, DEBOUNCE_MS);
  }, [query, mode, restrictToCountryCode, resetSuggestions, selectedLabel, maxSuggestions]);

  const handleSelectPrediction = async (prediction: PlacePrediction) => {
    clearDebounce();
    resetSuggestions();
    setQuery(prediction.description);
    setSelectedLabel(prediction.description);
    setIsResolving(true);
    setError(null);

    const terminatingSessionToken = sessionTokenRef.current;
    sessionTokenRef.current = null; // Session concludes upon place selection

    try {
      if (onEasyModePlaceSelect) {
        const place = await resolveEasyModePlaceDetails(
          prediction.placeId,
          prediction.mainText,
          terminatingSessionToken
        );
        syncedPlaceIdRef.current = place.placeId;
        const nextLabel = place.address || place.name;
        setQuery(nextLabel);
        setSelectedLabel(nextLabel);
        onEasyModePlaceSelect(place);
      } else {
        const place = await resolvePlaceDetails(
          prediction.placeId,
          terminatingSessionToken
        );
        const nextLabel = toLocationDisplayLabel(place);
        syncedPlaceIdRef.current = place.placeId;
        setQuery(nextLabel);
        setSelectedLabel(nextLabel);
        onPlaceSelect?.(place);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to resolve selected place"
      );
      setSelectedLabel("");
    } finally {
      setIsResolving(false);
    }
  };

  const handleInputChange = (nextValue: string) => {
    onInteraction?.();
    setQuery(nextValue);
    if (nextValue.trim() !== selectedLabel.trim()) {
      setSelectedLabel("");
    }
  };

  const handleClear = () => {
    clearDebounce();
    resetSuggestions();
    sessionTokenRef.current = null;
    setQuery("");
    setSelectedLabel("");
    syncedPlaceIdRef.current = null;
    setError(null);
    onInteraction?.();
    onClear?.();
  };

  const showClearButton =
    clearable &&
    !disabled &&
    !isResolving &&
    !isLoading &&
    !endAdornment &&
    Boolean(query.trim() || value);

  const showRightSpinner = isSearching || isResolving || isLoading;

  return (
    <div ref={containerRef} className={cn("space-y-2", className)}>
      {!hideLabel && (
        <Label htmlFor={id} className="text-sm font-semibold text-foreground">
          {label}
        </Label>
      )}
      <div
        className={cn(
          "relative",
          endAdornment &&
            "flex flex-col rounded-xl border border-gray-200 bg-white focus-within:border-[#1A5336]/50 focus-within:ring-2 focus-within:ring-[#1A5336]/15 sm:flex-row sm:items-center sm:gap-1",
          fieldClassName
        )}
      >
        <MapPin
          className={cn(
            "pointer-events-none absolute left-3 z-10 h-4 w-4 text-muted-foreground",
            endAdornment
              ? "top-3.5 sm:top-1/2 sm:-translate-y-1/2"
              : "top-1/2 -translate-y-1/2",
            pinClassName
          )}
          aria-hidden
        />
        <Input
          id={id}
          type="text"
          value={query}
          placeholder={placeholder}
          disabled={disabled || isResolving || isLoading}
          autoComplete="off"
          role="combobox"
          aria-label={hideLabel ? label : undefined}
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          className={cn(
            "min-w-0 flex-1 pl-9",
            endAdornment
              ? "border-0 bg-transparent pr-3 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
              : showRightSpinner || showClearButton
                ? "pr-10"
                : "pr-3",
            inputClassName
          )}
          onChange={(event) => handleInputChange(event.target.value)}
          onFocus={() => {
            if (predictions.length > 0) setIsOpen(true);
          }}
        />
        {endAdornment ? (
          <div className="relative z-10 flex shrink-0 items-center justify-end gap-1.5 border-t border-gray-100 px-2.5 py-1.5 sm:border-0 sm:py-1 sm:pr-2.5">
            {showRightSpinner && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {endAdornment}
          </div>
        ) : (
          <>
            {showRightSpinner && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
            {!showRightSpinner && showClearButton && (
              <button
                type="button"
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                onClick={handleClear}
                aria-label={`Clear ${label}`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </>
        )}

        {isOpen && predictions.length > 0 && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-[110] max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white text-gray-900 shadow-lg"
          >
            {predictions.map((prediction) => (
              <li key={prediction.placeId} role="option">
                <button
                  type="button"
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left text-sm hover:bg-gray-50"
                  onMouseDown={(event) => event.preventDefault()}
                  onTouchStart={(event) => event.preventDefault()}
                  onClick={() => handleSelectPrediction(prediction)}
                >
                  <span className="font-medium">{prediction.mainText}</span>
                  {prediction.secondaryText && (
                    <span className="text-xs text-gray-500">
                      {prediction.secondaryText}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** Backward-compatible alias */
export const PlaceAutocompleteInput = LocationAutocomplete;
